import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import type { IntakeSide } from './intakeSides';
import type { PreOrderContractorView } from '../../pre-order/preOrderContractorApi';
import type { OpenQuestion, RequiredFieldDef } from '../../shared/requiredFields';

/**
 * Przyjęcie zleceń w wersji lekkiej — klient API (`apps/api/src/intake/`,
 * makieta back office v2, fala B, B1, 21.10.2026): pulpit z kolejkami, obieg
 * CS → Administracja ze zwrotem i okno kontroli. Filtry kolejek i napisy
 * przychodzą z API — front nie liczy ich sam.
 */

/** Klucz filtra listy prezleceń — kontrakt z API (react-admin go pamięta). */
export const INTAKE_QUEUE_FILTER = 'intakeQueue';

/** Kolejki działów — napisy jak w API (`intake-queue.ts`). */
export const INTAKE_QUEUE_CHOICES: ReadonlyArray<{ id: string; name: string; side: IntakeSide }> = [
  { id: 'cs_todo', name: 'Do uzupełnienia (CS)', side: 'cs' },
  { id: 'cs_returned', name: 'Zwrócone z pytaniem (CS)', side: 'cs' },
  { id: 'adm_control', name: 'Do kontroli (Administracja)', side: 'administration' },
  { id: 'adm_approved', name: 'Zatwierdzone, przed plikiem', side: 'administration' },
  { id: 'adm_failed', name: 'Błąd zapisu do VIA', side: 'administration' },
  // Automatyczne ponawianie dostarczenia (21.10.2026, `viaDeliveryAutoRetry`).
  { id: 'adm_retrying', name: 'Błąd zapisu — ponawiane automatycznie', side: 'administration' },
  { id: 'adm_waiting_tms', name: 'Plik wytworzony, czeka na TMS', side: 'administration' },
];

/** Opis kafelka pulpitu — co znaczy liczba. */
export const INTAKE_TILE_DESCRIPTIONS: Record<string, string> = {
  cs_todo: 'prezlecenia u CS — uzupełnij i przekaż do Administracji',
  cs_returned: 'Administracja pyta — odpowiedz i przekaż ponownie',
  adm_control: 'przekazane przez CS — okno kontroli i zatwierdzenie zapisu',
  adm_approved: 'zatwierdzone do zapisu, plik dla VIA jeszcze nie powstał',
  adm_failed: 'plik nie dotarł — popraw i ponów',
  adm_retrying: 'plik nie dotarł — aplikacja ponawia dostarczenie sama; po wyczerpaniu prób trafi do „Błąd zapisu”',
  adm_waiting_tms: 'czeka, aż import z TMS pokaże zlecenie („Jest w TMS”)',
  ftlNotesOpen: 'uwagi planistów przy zleceniach — nowe i w toku',
  changesPendingDecision: 'zmiany „było → ma być” czekające na decyzję',
  changesToEnter: 'zaakceptowane — wprowadź w TMS i oznacz',
  changesReminder: 'po imporcie TMS pokazuje inną wartość niż „ma być”',
};

/** Kafelki, które wymagają reakcji — wyróżnione, gdy liczba > 0. */
export const INTAKE_TILE_HIGHLIGHT: Record<string, 'uwaga' | 'ostrzezenie'> = {
  cs_returned: 'ostrzezenie',
  adm_control: 'uwaga',
  adm_failed: 'ostrzezenie',
  changesReminder: 'ostrzezenie',
};

export interface IntakeTile {
  key: string;
  side: IntakeSide;
  label: string;
  target: 'pre-orders' | 'ftl-notes' | 'order-changes';
  filter: Record<string, unknown>;
  count: number;
}

export interface IntakeDashboardSummary {
  sides: IntakeSide[];
  tiles: IntakeTile[];
  computedAt: string;
}

export async function fetchIntakeDashboard(): Promise<IntakeDashboardSummary> {
  const { data } = await axiosNest.get<IntakeDashboardSummary>('/intake/dashboard');
  return {
    sides: Array.isArray(data?.sides) ? data.sides : [],
    tiles: Array.isArray(data?.tiles) ? data.tiles : [],
    computedAt: data?.computedAt ?? '',
  };
}

export interface IntakeMessage {
  id: number;
  kind: string;
  label: string;
  message: string | null;
  fromStage: string | null;
  toStage: string | null;
  authorName: string | null;
  createdAt: string;
  /** Pole pytania przy zwrocie (24.10.2026, `intakeQuestionField`); `null` — ogólne. */
  field?: string | null;
  fieldLabel?: string;
}

/** Obieg prezlecenia — `GET /intake/pre-orders/:id/flow`. */
export interface IntakeFlow {
  preOrderId: number;
  stage: 'cs' | 'administration' | 'returned_to_cs';
  state: 'cs' | 'administration' | 'returned_to_cs' | 'approved';
  stateLabel: string;
  department: string;
  holderSide: IntakeSide;
  since: string | null;
  by: string | null;
  returnCount: number;
  messages: IntakeMessage[];
  blockers: string[];
  mySides: IntakeSide[];
  handoff: { allowed: boolean; refusal: string | null };
  returnToCs: { allowed: boolean; refusal: string | null };
  /**
   * Pytanie przypięte do pola (24.10.2026, `intakeQuestionField`) — tylko
   * z przełącznikiem: pola do wyboru przy zwrocie i otwarte pytanie.
   */
  questionFields?: RequiredFieldDef[];
  openQuestion?: OpenQuestion | null;
}

export async function fetchIntakeFlow(preOrderId: number | string): Promise<IntakeFlow> {
  const { data } = await axiosNest.get<IntakeFlow>(`/intake/pre-orders/${preOrderId}/flow`);
  return data;
}

export async function handoffPreOrder(
  preOrderId: number | string,
  message: string,
): Promise<IntakeFlow> {
  const { data } = await axiosNest.post<IntakeFlow>(`/intake/pre-orders/${preOrderId}/handoff`, {
    message: message.trim() === '' ? undefined : message,
  });
  return data;
}

export async function returnPreOrderToCs(
  preOrderId: number | string,
  question: string,
  /** Pole pytania (24.10.2026); brak — pytanie ogólne. */
  field?: string | null,
): Promise<IntakeFlow> {
  const { data } = await axiosNest.post<IntakeFlow>(`/intake/pre-orders/${preOrderId}/return`, {
    question,
    ...(field ? { field } : {}),
  });
  return data;
}

/** Plik w podglądzie zapisu (jak `ViaWritePreview.files`). */
export interface ControlFile {
  kind: string;
  label: string;
  fileName: string;
  xml: string;
  errors: string[];
}

/** Okno kontroli — `GET /intake/pre-orders/:id/control`. */
export interface IntakeControlWindow {
  preOrder: {
    id: number;
    clientName: string | null;
    customerOrderNumber: string | null;
    relation: string | null;
    loadingCity: string | null;
    loadingCountry: string | null;
    unloadingCity: string | null;
    unloadingCountry: string | null;
    loadingDate: string | null;
    unloadingDate: string | null;
    requiredVehicleType: string | null;
    status: string;
    acceptedAt: string | null;
    acceptedBy: string | null;
    missingFields: string[];
    company: { id: number; name: string | null; country: string | null } | null;
    bankRowId: number | null;
  };
  copy: { from: { id: number; clientName: string | null } | null; confirmedKept: string | null };
  order: {
    id: number;
    orderNumber: string | null;
    viaWriteStatus: string;
    stateLabel: string;
    approvedBy: string | null;
    approvedAt: string | null;
    tmsConfirmedNumber: string | null;
    tmsConfirmedAt: string | null;
  } | null;
  write: {
    errors: string[];
    ostrzezenia?: string[];
    files?: ControlFile[];
    duplicates?: {
      checked: boolean;
      uncheckedReason: string | null;
      candidates: unknown[];
      unconfirmed: number;
      writeRefusal: string | null;
    } | null;
    canWrite?: boolean;
    blockReason?: string | null;
  } | null;
  blockers: string[];
  vat: { rate: string | null; reason: string; certain: boolean; missing: string[] };
  /**
   * Kontrahent z kartoteki i źródło do VAT (fala B, 21.10.2026,
   * `preOrderContractor`); bez przełącznika — `null`.
   */
  contractor?: PreOrderContractorView | null;
  stage: {
    code: string;
    label: string;
    at: string | null;
    atLabel: string;
    detail: string;
    failed: boolean;
    department: string | null;
  } | null;
  flow: IntakeFlow | null;
  resultNote: string;
}

export async function fetchControlWindow(preOrderId: number | string): Promise<IntakeControlWindow> {
  const { data } = await axiosNest.get<IntakeControlWindow>(`/intake/pre-orders/${preOrderId}/control`);
  return data;
}

/** Adres okna kontroli w zasobie prezleceń (ścieżka podrzędna, jak kopia). */
export const CONTROL_PATH = ':id/control';
export const controlPath = (preOrderId: number | string) => `/pre-orders/${preOrderId}/control`;

export { toErrorMessage };
